import { List, ListItem, ListItemButton, ListItemText, Collapse } from '@mui/material';
import { ODSCollapseDown, ODSCollapseUp } from '@telekom-ods/react-icons';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';

import type { NavMenuItem } from '@/types';

interface SidebarNavigationItemProps {
  item: NavMenuItem;
  currentPath: string;
  level?: number;
}

const SidebarNavigationItem: React.FC<SidebarNavigationItemProps> = ({
  item,
  currentPath,
  level = 0,
}) => {
  const hasSubMenu = item.children?.length ? item.children.length > 0 : false;
  const isSelected = currentPath === (item.path ?? '');
  const [isOpen, setIsOpen] = useState<boolean>(isSelected);

  const isAncestor = useCallback((path: string, menuItem: NavMenuItem): boolean => {
    if (path === menuItem.path) return true;
    return menuItem.children?.some(child => isAncestor(path, child)) ?? false;
  }, []);

  useEffect(() => {
    if (hasSubMenu && isAncestor(currentPath, item)) {
      setIsOpen(true);
    } else if (!isAncestor(currentPath, item)) {
      setIsOpen(false);
    }
  }, [currentPath, hasSubMenu, isAncestor, item]);

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          component={Link}
          to={item.path ?? ''}
          onClick={() => {
            setIsOpen(prevOpen => !prevOpen);
          }}
          sx={{
            pl: level * 2 + 2,
            borderLeftColor: isSelected ? 'primary.main' : 'transparent',
          }}
        >
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{ color: isSelected ? 'primary' : 'inherit' }}
          />
          {hasSubMenu && (isOpen ? <ODSCollapseUp /> : <ODSCollapseDown />)}
        </ListItemButton>
      </ListItem>

      {hasSubMenu && (
        <Collapse
          in={isOpen}
          timeout='auto'
          unmountOnExit
        >
          <List
            component='div'
            disablePadding
          >
            {item.children?.map(child => (
              <SidebarNavigationItem
                key={child.title}
                item={child}
                currentPath={currentPath}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

interface SidebarNavigationProps {
  menuItems: NavMenuItem[];
  currentPath?: string;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({ menuItems, currentPath = '' }) => (
  <List component='nav'>
    {menuItems.map(item => (
      <SidebarNavigationItem
        key={item.title}
        item={item}
        currentPath={currentPath}
      />
    ))}
  </List>
);

export default SidebarNavigation;
